import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Phone, ExternalLink, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Vaccination", "UTI Consult", "Women's Health Consult", "Woundcare & Skin Consult",
  "Compounding Service", "Beauty & Make-up Service", "Ear Piercing", "Naturopath Consult",
  "Pain & Diabetic Consult", "Asthma Review", "COPD Consult", "Heart Health Consult",
  "Diabetic Management Consult", "Ear Infection Consult", "Hormonal Contraception",
  "Eczema / Dermatitis Consult", "Minor Wound Management", "Travel Health", "Other",
];

const EMPTY = { name: "", phone: "", email: "", service: "", preferred_date: "", preferred_time: "", notes: "", age: "" };

const TIME_SLOTS = [];
for (let h = 8; h <= 17; h++) {
  for (const m of [0, 15, 30, 45]) {
    if (h === 17 && m > 30) break;
    TIME_SLOTS.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

const fmt12 = (t) => {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "pm" : "am"}`;
};

export default function BookingSection() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("preselect-service", handler);
    return () => window.removeEventListener("preselect-service", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please choose a service");
      return;
    }
    if (form.service === "Ear Piercing" && (!form.age || Number(form.age) < 7)) {
      toast.error("Ear piercing is available for ages 7 and over — please enter the patient's age");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, { ...form, age: form.age ? Number(form.age) : null });
      setConfirmed(res.data.booking_id);
      toast.success("Booking request sent — we'll confirm shortly");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong — please call us on 9757 2224");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink" data-testid="booking-eyebrow">Book a Service</p>
          <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="booking-heading">
            Book online, or just call us
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            Choose whichever suits you — book instantly through HealthEngine, request a booking below and our team
            will confirm, or simply give us a call.
          </p>

          <div className="mt-9 space-y-4">
            <a
              href="https://healthengine.com.au/book/95540"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-healthengine-link"
              className="flex items-center justify-between rounded-2xl border border-brand-pink/30 bg-brand-blush/50 p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(229,0,126,0.12)]"
            >
              <div className="flex items-center gap-4">
                <CalendarCheck className="h-7 w-7 text-brand-pink" />
                <div>
                  <p className="font-heading font-bold">Book instantly on HealthEngine</p>
                  <p className="text-sm text-muted-foreground">Live availability for all our services</p>
                </div>
              </div>
              <ExternalLink className="h-5 w-5 text-brand-pink" />
            </a>

            <a
              href="tel:+61897572224"
              data-testid="booking-call-card"
              className="flex items-center gap-4 rounded-2xl border border-border/60 bg-white p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
            >
              <Phone className="h-7 w-7 text-brand-green" />
              <div>
                <p className="font-heading text-xl font-extrabold tracking-tight">(08) 9757 2224</p>
                <p className="text-sm text-muted-foreground">Prefer to talk? Call to book any service</p>
              </div>
            </a>

            <div className="flex items-center gap-4 rounded-2xl border border-border/60 bg-white p-5" data-testid="booking-hours-card">
              <Clock className="h-7 w-7 text-brand-green" />
              <div>
                <p className="font-heading font-bold">Open 7 days</p>
                <p className="text-sm text-muted-foreground">Monday to Sunday, 8:00am – 6:00pm</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-3xl border border-border/60 bg-white p-8 shadow-[0_16px_48px_rgba(0,0,0,0.06)] lg:p-10" data-testid="booking-form-card">
            {confirmed ? (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center text-center" data-testid="booking-success">
                <CheckCircle2 className="h-14 w-14 text-brand-green" />
                <h3 className="mt-6 font-heading text-2xl font-bold">Request received</h3>
                <p className="mt-3 max-w-sm text-sm text-muted-foreground">
                  Thanks — our team has your request (ref {confirmed.slice(0, 8)}) and will call or email to confirm
                  your appointment. Need it sooner? Call <a href="tel:+61897572224" className="font-semibold text-brand-pink">9757 2224</a>.
                </p>
                <Button
                  variant="outline"
                  data-testid="booking-another-btn"
                  onClick={() => setConfirmed(null)}
                  className="mt-8 rounded-full px-6 font-heading font-semibold"
                >
                  Make another booking
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                <h3 className="font-heading text-xl font-bold">Request a booking</h3>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="bk-name">Full name</Label>
                    <Input id="bk-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Citizen" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bk-phone">Phone</Label>
                    <Input id="bk-phone" data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bk-email">Email</Label>
                  <Input id="bk-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bk-service">Service</Label>
                  <select
                    id="bk-service"
                    data-testid="booking-service-select"
                    required
                    value={form.service}
                    onChange={set("service")}
                    className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="" disabled>Choose a service…</option>
                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bk-age">
                    Patient age {form.service === "Ear Piercing" ? "(required — ear piercing is for ages 7+)" : "(optional)"}
                  </Label>
                  <Input
                    id="bk-age"
                    data-testid="booking-age-input"
                    type="number"
                    min="0"
                    max="120"
                    value={form.age}
                    onChange={set("age")}
                    placeholder="e.g. 34"
                    className="rounded-xl"
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="bk-date">Preferred date</Label>
                    <Input id="bk-date" data-testid="booking-date-input" required type="date" value={form.preferred_date} onChange={set("preferred_date")} className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bk-time">Preferred time</Label>
                    <select
                      id="bk-time"
                      data-testid="booking-time-select"
                      required
                      value={form.preferred_time}
                      onChange={set("preferred_time")}
                      className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="" disabled>Choose a time…</option>
                      {TIME_SLOTS.map((t) => (
                        <option key={t} value={t}>{fmt12(t)}</option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">Every 15 minutes, between 8:00am and 5:30pm</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bk-notes">Notes (optional)</Label>
                  <Textarea id="bk-notes" data-testid="booking-notes-input" rows={3} value={form.notes} onChange={set("notes")} placeholder="Anything we should know before your visit…" className="rounded-xl" />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit-btn"
                  className="w-full rounded-full bg-brand-pink py-6 font-heading text-base font-semibold text-white hover:bg-brand-pinkdark disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send booking request"}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  We'll confirm by phone or email. For same-day appointments, call 9757 2224.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
